import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchMyReferralCode, fetchReferrals, createReferral, fetchProjects, fetchAppLinks } from "../api/api";

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

const EMPTY_FORM = {
  referred_name: "",
  referred_mobile: "",
  referred_email: "",
  project: "",
  notes: "",
};

function Referral() {

  const [referralCode, setReferralCode] = useState("");
  const [projects, setProjects] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [copied, setCopied] = useState(false);

  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareSuccess, setShareSuccess] = useState("");

  const [activeTab, setActiveTab] = useState("projects");
  const [playStoreUrl, setPlayStoreUrl] = useState(null);
  const [appStoreUrl, setAppStoreUrl] = useState(null);
  const [appShareStatus, setAppShareStatus] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [code, referralData, projectData, appLinks] = await Promise.all([
        fetchMyReferralCode(),
        fetchReferrals(),
        fetchProjects().catch(() => []),
        fetchAppLinks().catch(() => ({ play_store_url: null, app_store_url: null })),
      ]);
      setReferralCode(code);
      setReferrals(referralData);
      setProjects(projectData);
      setPlayStoreUrl(appLinks.play_store_url);
      setAppStoreUrl(appLinks.app_store_url);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      window.alert("Could not copy — please copy the code manually.");
    }
  };

  const handleShareApp = async () => {
    if (!playStoreUrl && !appStoreUrl) return;

    const lines = [
      "Check out the Jain Farms app — manage your farmland, book visits, and raise service requests, all in one place!",
    ];
    if (playStoreUrl) {
      // Appends this referrer's code as Play's install-referrer string,
      // read back by the app after install (see captureInstallReferralCode
      // in the mobile app's api.js) to credit this referrer once the
      // friend registers — same trick as mobile's handleShareApp.
      const url = referralCode
        ? `${playStoreUrl}&referrer=${encodeURIComponent(`referrer_code=${referralCode}`)}`
        : playStoreUrl;
      lines.push(`Android: ${url}`);
    }
    // No App Store equivalent of the install-referrer trick — omitted
    // entirely while APP_STORE_URL is unset, since there's no iOS app
    // planned right now.
    if (appStoreUrl) {
      lines.push(`iPhone: ${appStoreUrl}`);
    }
    const text = lines.join("\n");

    setAppShareStatus("");
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        setAppShareStatus("Copied to clipboard!");
        setTimeout(() => setAppShareStatus(""), 2000);
      }
    } catch (err) {
      // AbortError just means the user closed the native share sheet — not an error.
      if (err.name !== "AbortError") {
        window.alert("Could not share — please try again.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!form.referred_name.trim()) {
      setFormError("Please enter your friend's name.");
      return;
    }

    setSubmitting(true);
    try {
      const created = await createReferral({
        referred_name: form.referred_name.trim(),
        referred_mobile: form.referred_mobile.trim() || undefined,
        referred_email: form.referred_email.trim() || undefined,
        project: form.project || undefined,
        notes: form.notes.trim() || undefined,
      });
      setForm(EMPTY_FORM);
      setFormSuccess(
        form.referred_email.trim()
          ? (created.email_sent
              ? "Referral submitted — an email invite with your code was sent."
              : "Referral submitted — our team will follow up (email invite could not be sent).")
          : "Referral submitted — our team will follow up."
      );
      const referralData = await fetchReferrals();
      setReferrals(referralData);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickShare = async (e) => {
    e.preventDefault();
    setShareError("");
    setShareSuccess("");

    if (!shareEmail.trim()) {
      setShareError("Please enter your friend's email.");
      return;
    }

    setSharing(true);
    try {
      const created = await createReferral({
        referred_name: shareEmail.trim().split("@")[0],
        referred_email: shareEmail.trim(),
        notes: "Shared via the Refer a Friend page's quick-share button.",
      });
      setShareEmail("");
      setShareSuccess(
        created.email_sent
          ? "Your referral code was emailed to your friend."
          : "Referral logged, but the email could not be sent right now."
      );
      const referralData = await fetchReferrals();
      setReferrals(referralData);
    } catch (err) {
      setShareError(errorMessage(err));
    } finally {
      setSharing(false);
    }
  };

  const projectReferrals = referrals.filter((r) => r.referral_type !== "app");
  const appReferrals = referrals.filter((r) => r.referral_type === "app");

  const totalReferrals = projectReferrals.length;
  const rewardedCount = projectReferrals.filter((r) => r.reward_given).length;

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">

        <div className="referral-page">

          <div className="referral-header">
            <h2>Refer a Friend</h2>
            <p>
              Earn rewards for every referral
            </p>
          </div>

          {loadError && <p className="login-error" style={{ color: "red" }}>{loadError}</p>}

          <div className="referral-tabs">
            <button
              type="button"
              className={`referral-tab-btn${activeTab === "projects" ? " active" : ""}`}
              onClick={() => setActiveTab("projects")}
            >
              Refer Jain Farms Projects
            </button>
            <button
              type="button"
              className={`referral-tab-btn${activeTab === "app" ? " active" : ""}`}
              onClick={() => setActiveTab("app")}
            >
              Refer the App
            </button>
          </div>

          {activeTab === "app" && (
            <div className="app-referral-card">
              <h3>Refer the Jain Farms App</h3>
              <p>
                {playStoreUrl || appStoreUrl
                  ? "Share the Jain Farms app with your friends and family."
                  : "Share the Jain Farms app with your friends and family. The app isn't live on the stores yet — links will go here as soon as it's published."}
              </p>
              {(playStoreUrl || appStoreUrl) && (
                <div className="store-badges">
                  {playStoreUrl && (
                    <a
                      className="store-badge-btn enabled"
                      href={playStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>Download on</span>
                      <strong>Google Play</strong>
                    </a>
                  )}
                  {appStoreUrl && (
                    <a
                      className="store-badge-btn enabled"
                      href={appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>Download on</span>
                      <strong>App Store</strong>
                    </a>
                  )}
                </div>
              )}

              <button
                type="button"
                className="copy-code-btn"
                style={{ marginTop: "16px" }}
                onClick={handleShareApp}
                disabled={!playStoreUrl && !appStoreUrl}
              >
                📤 Share the App
              </button>
              {appShareStatus && (
                <p style={{ color: "#166534", fontWeight: 600, marginTop: "8px" }}>{appShareStatus}</p>
              )}

              <div className="my-referrals" style={{ textAlign: "left", marginTop: "20px" }}>
                <h3>My App Referrals</h3>

                {loading && <p>Loading…</p>}
                {!loading && appReferrals.length === 0 && <p>No app referrals yet — share the app to get started.</p>}

                {appReferrals.map((item) => (
                  <div key={item.id} className="referral-item">
                    <div>
                      <h4>{item.referred_name || "App referral"}</h4>
                      <p>Shared {new Date(item.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="referral-status">
                      <span className={item.status_name === "Successful" ? "converted" : "contacted"}>
                        {item.status_name || "-"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "projects" && (
          <>
          <div className="referral-code-card">

            <h3>Your Referral Code</h3>

            <div className="referral-code-box">
              {loading ? "…" : referralCode}
            </div>

            <button className="copy-code-btn" onClick={handleCopyCode} disabled={loading || !referralCode}>
              {copied ? "Copied!" : "Copy Code"}
            </button>

            {shareError && <p className="login-error" style={{ color: "red", marginTop: "10px" }}>{shareError}</p>}
            {shareSuccess && <p style={{ color: "#166534", fontWeight: 600, marginTop: "10px" }}>{shareSuccess}</p>}

            <form onSubmit={handleQuickShare} style={{ display: "flex", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
              <input
                type="email"
                placeholder="Friend's email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                style={{ flex: 1, minWidth: "180px" }}
              />
              <button type="submit" className="copy-code-btn" disabled={sharing || loading}>
                {sharing ? "Sharing…" : "✉️ Share via Email"}
              </button>
            </form>

          </div>

          <div className="referral-card">

            <h3>New Referral</h3>

            {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}
            {formSuccess && <p style={{ color: "#166534", fontWeight: 600 }}>{formSuccess}</p>}

            <form onSubmit={handleSubmit}>

              <label>Full Name</label>
              <input
                type="text"
                name="referred_name"
                placeholder="Friend's full name"
                value={form.referred_name}
                onChange={handleChange}
              />

              <label>Mobile Number</label>
              <input
                type="text"
                name="referred_mobile"
                placeholder="+91 XXXXX XXXXX"
                value={form.referred_mobile}
                onChange={handleChange}
              />

              <label>Email (Optional)</label>
              <input
                type="email"
                name="referred_email"
                placeholder="friend@email.com"
                value={form.referred_email}
                onChange={handleChange}
              />

              <label>Preferred Project</label>
              <select name="project" value={form.project} onChange={handleChange}>
                <option value="">Select a project (optional)</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>

              <label>Notes (Optional)</label>
              <textarea
                name="notes"
                rows="4"
                placeholder="How do they know about us?"
                value={form.notes}
                onChange={handleChange}
              />

              <button type="submit" className="submit-referral-btn" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit Referral"}
              </button>

            </form>

          </div>

          <div className="referral-summary">
            <div className="summary-card">
              <h3>{loading ? "…" : totalReferrals}</h3>
              <p>Total Referrals</p>
            </div>

            <div className="summary-card">
              <h3>{loading ? "…" : rewardedCount}</h3>
              <p>Rewarded</p>
            </div>
          </div>

          <div className="my-referrals">

            <h3>My Referrals</h3>

            {loading && <p>Loading…</p>}
            {!loading && projectReferrals.length === 0 && <p>No referrals submitted yet.</p>}

            {projectReferrals.map((item) => (

              <div
                key={item.id}
                className="referral-item"
              >

                <div>

                  <h4>{item.referred_name}</h4>

                  <p>
                    Submitted {new Date(item.created_at).toLocaleDateString()}
                    {item.project ? ` • ${item.project}` : ""}
                  </p>

                </div>

                <div className="referral-status">

                  <span
                    className={
                      item.status_name === "Purchased"
                        ? "converted"
                        : "contacted"
                    }
                  >
                    {item.status_name || "—"}
                  </span>

                  <p>{item.reward_given ? "Reward given" : "Reward pending"}</p>

                </div>

              </div>

            ))}

          </div>
          </>
          )}

        </div>

      </div>
    </>
  );
}

export default Referral;
